import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import Layout from './components/Layout.tsx';
import ScrollToTop from './components/ScrollToTop.tsx';
const Legal = lazy(() => import('./Legal.tsx'));
const Articles = lazy(() => import('./Articles.tsx'));
const TroubleshootingPage = lazy(() => import('./TroubleshootingPage.tsx'));
const ArticleDetail = lazy(() => import('./ArticleDetail.tsx'));
const ReleaseNotes = lazy(() => import('./ReleaseNotes.tsx'));
const FAQ = lazy(() => import('./FAQ.tsx'));
const CookiePolicy = lazy(() => import('./CookiePolicy.tsx'));
const Plugins = lazy(() => import('./Plugins.tsx'));
const ConversionPage = lazy(() => import('./ConversionPage.tsx'));
const NotFound = lazy(() => import('./NotFound.tsx'));
import { initFormatSupport } from './services/imageService';
import './i18n';
import './index.css';

initFormatSupport();
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Loading component for Suspense
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Language wrapper component to handle language detection from URL
const LanguageWrapper = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const params = useParams();
  const langParam = params.lang;
  
  useEffect(() => {
    if (langParam && i18n.language !== langParam) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);
  
  return <>{children}</>;
};

// Get the root element
const rootElement = document.getElementById('root')!;

// Define the app content
const AppContent = (
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter basename="/">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:text-primary-900 focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
          Skip to main content
        </a>
        <ScrollToTop />
        <Routes>
          {/* Root redirect to default language */}
          <Route path="/" element={<Navigate to="/en" replace />} />
          
          {/* Language-prefixed routes wrapped in shared Layout */}
          <Route path="/:lang" element={<LanguageWrapper><Layout /></LanguageWrapper>}>
            <Route index element={<App />} />
            <Route path="convert/:slug" element={<Suspense fallback={<LoadingFallback />}><ConversionPage /></Suspense>} />
            <Route path="legal" element={<Suspense fallback={<LoadingFallback />}><Legal /></Suspense>} />
            <Route path="troubleshooting" element={<Suspense fallback={<LoadingFallback />}><TroubleshootingPage /></Suspense>} />
            <Route path="articles" element={<Suspense fallback={<LoadingFallback />}><Articles /></Suspense>} />
            <Route path="articles/:slug" element={<Suspense fallback={<LoadingFallback />}><ArticleDetail /></Suspense>} />
            <Route path="plugins" element={<Suspense fallback={<LoadingFallback />}><Plugins /></Suspense>} />
            <Route path="cookie-policy" element={<Suspense fallback={<LoadingFallback />}><CookiePolicy /></Suspense>} />
            <Route path="release-notes" element={<Suspense fallback={<LoadingFallback />}><ReleaseNotes /></Suspense>} />
            <Route path="faq" element={<Suspense fallback={<LoadingFallback />}><FAQ /></Suspense>} />
            <Route path="404" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
          </Route>
          
          {/* Legacy non-prefixed routes (redirect to language-prefixed) */}
          <Route path="/convert/:slug" element={<Navigate to={`/en/convert/${window.location.pathname.split('/').pop()}`} replace />} />
          <Route path="/legal" element={<Navigate to="/en/legal" replace />} />
          <Route path="/troubleshooting" element={<Navigate to="/en/troubleshooting" replace />} />
          <Route path="/articles" element={<Navigate to="/en/articles" replace />} />
          <Route path="/articles/:slug" element={<Navigate to={`/en/articles/${window.location.pathname.split('/').pop()}`} replace />} />
          <Route path="/plugins" element={<Navigate to="/en/plugins" replace />} />
          <Route path="/cookie-policy" element={<Navigate to="/en/cookie-policy" replace />} />
          <Route path="/faq" element={<Navigate to="/en/faq" replace />} />
          <Route path="/release-notes" element={<Navigate to="/en/release-notes" replace />} />
          
          {/* 404 route */}
          <Route path="*" element={<Navigate to="/en/404" replace />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>
);

// Render the app - React 18 handles hydration automatically
createRoot(rootElement).render(AppContent);